'use client';

import { Box, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '../../../../components/button';
import { Collapsible } from '../../../../components/collapsible';
import { Text } from '../../../../components/styled/text';
import { type TracePhase } from '../types';
import { type TraceReadFileEntry } from '../utils/read-file-tool';
import { TraceReadFileContent } from './TraceReadFileContent';

type TraceReadFilesPanelProps = {
  entries: TraceReadFileEntry[];
  tracePhase?: TracePhase;
};

export function TraceReadFilesPanel({
  entries,
  tracePhase = 'attack',
}: TraceReadFilesPanelProps) {
  const t = useTranslations('designSystem.chat');
  const tCommon = useTranslations('designSystem.common');
  const isCoPilotTrace = tracePhase === 'co-pilot';
  const [openEntryIds, setOpenEntryIds] = useState<Set<string>>(
    () => new Set(isCoPilotTrace ? entries.map((entry) => entry.id) : []),
  );
  const toggleEntry = (entryId: string, open: boolean) => {
    setOpenEntryIds((currentEntryIds) => {
      const nextEntryIds = new Set(currentEntryIds);

      if (open) {
        nextEntryIds.add(entryId);
      } else {
        nextEntryIds.delete(entryId);
      }

      return nextEntryIds;
    });
  };

  return (
    <Box
      bg={isCoPilotTrace ? 'transparent' : 'bg.subtle'}
      borderWidth={isCoPilotTrace ? '0' : '1px'}
      borderColor="border.neutral.muted"
      borderRadius="md"
      px={isCoPilotTrace ? 0 : 3}
      py={isCoPilotTrace ? 0 : 2}
      width="100%"
    >
      <VStack alignItems="stretch" gap={isCoPilotTrace ? 2 : 0.5} width="100%">
        {entries.map((entry) => {
          const fileLabel = entry.displayName ?? t('readTool.unknownFile');

          if (isCoPilotTrace) {
            return (
              <Box key={entry.id} width="100%">
                <Text fontSize="xs" fontWeight="normal" color="fg.muted" pb={1}>
                  {fileLabel}
                </Text>
                <TraceReadFileContent
                  filePath={entry.filePath}
                  output={entry.output}
                  error={entry.error}
                  isMarkdown={entry.isMarkdown}
                  showPathHeader={Boolean(entry.filePath)}
                />
              </Box>
            );
          }

          const isOpen = openEntryIds.has(entry.id);

          return (
            <Collapsible.Root
              key={entry.id}
              open={isOpen}
              onOpenChange={({ open }) => toggleEntry(entry.id, open)}
              lazyMount
              unmountOnExit
            >
              <Collapsible.Trigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  width="100%"
                  gap={1}
                  alignItems="center"
                  justifyContent="flex-start"
                  minW={0}
                  height="auto"
                  px={0}
                  py={0.5}
                  color="fg.default"
                  cursor="pointer"
                  textAlign="left"
                  _hover={{ bg: 'transparent', color: 'fg.default' }}
                >
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="normal"
                    color="fg.muted"
                    flexShrink={0}
                  >
                    {tCommon('read')}
                  </Text>
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="normal"
                    color="fg.default"
                    truncate
                    minW={0}
                    title={entry.filePath}
                  >
                    {fileLabel}
                  </Text>
                  <Box
                    as="span"
                    display="flex"
                    alignItems="center"
                    color="fg.muted"
                    flexShrink={0}
                  >
                    {isOpen ? (
                      <ArrowDown01Icon size={12} />
                    ) : (
                      <ArrowRight01Icon size={12} />
                    )}
                  </Box>
                </Button>
              </Collapsible.Trigger>

              <Collapsible.Content>
                <Box py={1}>
                  <TraceReadFileContent
                    filePath={entry.filePath}
                    output={entry.output}
                    error={entry.error}
                    isMarkdown={entry.isMarkdown}
                    showPathHeader={false}
                  />
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          );
        })}
      </VStack>
    </Box>
  );
}
